(() => {
  const PAGE_CLASS = 'homepage-v20';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const initialized = new WeakSet();

  const reduceMotion = window.matchMedia(REDUCED_MOTION_QUERY);

  let counterObserver = null;
  let revealObserver = null;
  let sectionRevealObserver = null;

  function initializeReveals(root) {
    const targets = root.querySelectorAll?.('[data-v20-reveal]') || [];
    const sections = root.querySelectorAll?.('[data-v20-section-reveal]') || [];
    if (!targets.length && !sections.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-visible'));
      sections.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    revealObserver ??= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          if (entry.isIntersecting) {
            const stagger = Number(el.dataset.v20Reveal) || 0;
            const mod = Number(el.dataset.v20RevealMod) || 0;
            const siblings = [...el.parentElement.querySelectorAll(':scope > [data-v20-reveal]')];
            let index = siblings.indexOf(el);
            if (mod > 0) index %= mod;
            setTimeout(() => el.classList.add('is-visible'), Math.max(0, index) * stagger);
          } else {
            el.classList.remove('is-visible');
          }
        });
      },
      { threshold: 0.2 }
    );

    sectionRevealObserver ??= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          sectionRevealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach((el) => revealObserver.observe(el));
    sections.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) { el.classList.add('is-visible'); return; }
      sectionRevealObserver.observe(el);
    });
  }

  function animateCounter(element) {
    if (element.dataset.v20Counted === 'true') return;
    element.dataset.v20Counted = 'true';

    const target = Number.parseInt(element.dataset.v20Count, 10);
    const suffix = element.dataset.v20Suffix || '';

    if (!Number.isFinite(target) || reduceMotion.matches) {
      element.textContent = `${target}${suffix}`;
      return;
    }

    const start = element.dataset.v20CountStart != null
      ? Number(element.dataset.v20CountStart)
      : Math.max(0, target - Math.min(target, 18));
    const duration = Number(element.dataset.v20CountDuration) || 1200;
    const power = element.dataset.v20CountStart != null ? 2 : 3;
    let startedAt;

    function tick(timestamp) {
      if (!element.isConnected) return;
      startedAt ??= timestamp;
      const progress = Math.min((timestamp - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, power);
      element.textContent = `${Math.round(start + (target - start) * eased)}${suffix}`;
      if (progress < 1) requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }

  function initializeCounters(root) {
    const counters = root.querySelectorAll?.('[data-v20-count]') || [];

    if (!('IntersectionObserver' in window)) {
      counters.forEach(animateCounter);
      return;
    }

    counterObserver ??= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animateCounter(entry.target);
          counterObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    counters.forEach((counter) => {
      if (counter.dataset.v20Counted === 'true') return;
      counterObserver.observe(counter);
    });
  }

  function attachDeferredSource(video) {
    const source = video.querySelector('source[data-src]');
    if (source && !source.getAttribute('src')) {
      source.src = source.dataset.src;
      video.load();
    }
    if (!reduceMotion.matches) {
      const attempt = video.play();
      attempt?.catch?.(() => {});
    }
  }

  function initializeVideos(root) {
    root.querySelectorAll?.('[data-v20-autoplay]').forEach((video) => {
      if (reduceMotion.matches) {
        video.removeAttribute('autoplay');
        video.pause();
        return;
      }

      const mode = video.dataset.v20Deferred;

      if (mode === 'onload') {
        if (document.readyState === 'complete') {
          attachDeferredSource(video);
        } else if (!initialized.has(video)) {
          initialized.add(video);
          window.addEventListener('load', () => attachDeferredSource(video), { once: true });
        }
        return;
      }

      if (mode === 'visible') {
        if (initialized.has(video)) return;
        initialized.add(video);
        if (!('IntersectionObserver' in window)) {
          attachDeferredSource(video);
          return;
        }
        const observer = new IntersectionObserver(
          (entries, self) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              attachDeferredSource(entry.target);
              self.disconnect();
            });
          },
          { rootMargin: '600px 0px' }
        );
        observer.observe(video);
        return;
      }

      const attempt = video.play();
      attempt?.catch?.(() => {});
    });

    root.querySelectorAll?.('[data-v20-video-toggle]').forEach((button) => {
      if (initialized.has(button)) return;
      initialized.add(button);

      const video = button.closest('.v20-brand-video')?.querySelector('video');
      if (!video) return;
      const playIcon = button.querySelector('[data-v20-play-icon]');
      const pauseIcon = button.querySelector('[data-v20-pause-icon]');

      const syncIcons = () => {
        const playing = !video.paused;
        if (playIcon) playIcon.style.display = playing ? 'none' : '';
        if (pauseIcon) pauseIcon.style.display = playing ? '' : 'none';
        button.setAttribute('aria-label', playing ? 'Pause brand video' : 'Play brand video');
      };

      button.addEventListener('click', () => {
        if (video.paused) {
          const source = video.querySelector('source[data-src]');
          if (source && !source.getAttribute('src')) {
            source.src = source.dataset.src;
            video.load();
          }
          const attempt = video.play();
          attempt?.catch?.(() => {});
        } else {
          video.pause();
        }
        syncIcons();
      });
      video.addEventListener('play', syncIcons);
      video.addEventListener('pause', syncIcons);
      syncIcons();
    });
  }

  function formatTime(seconds) {
    const value = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
    const minutes = Math.floor(value / 60);
    const remainder = Math.floor(value % 60);
    return `${minutes}:${String(remainder).padStart(2, '0')}`;
  }

  function loadUserVideo(video) {
    if (video.dataset.v20Loaded === 'true') return;
    const source = video.querySelector('source[data-src]');
    if (!source) return;
    source.src = source.dataset.src;
    video.dataset.v20Loaded = 'true';
    video.load();
  }

  function pauseOtherUserVideos(activeVideo) {
    document.querySelectorAll('[data-v20-user-video]').forEach((video) => {
      if (video === activeVideo) return;
      video.pause();
      video.muted = true;
    });
  }

  function initializeUserVideos(root) {
    root.querySelectorAll?.('.v20-video-controls').forEach((controls) => {
      if (initialized.has(controls)) return;
      initialized.add(controls);

      const container = controls.parentElement;
      const video = container?.querySelector('[data-v20-user-video]');
      const playButton = controls.querySelector('[data-v20-user-play]');
      const playIcon = controls.querySelector('[data-v20-play-icon]');
      const pauseIcon = controls.querySelector('[data-v20-pause-icon]');
      const seek = controls.querySelector('[data-v20-user-seek]');
      const time = controls.querySelector('[data-v20-user-time]');
      if (!video || !playButton || !seek || !time) return;

      const ccButton = controls.querySelector('[data-v20-cc]');
      const captionTrack = video.querySelector('track[data-v20-caption-track]');
      if (ccButton && captionTrack) {
        captionTrack.track.mode = 'hidden';
        ccButton.addEventListener('click', (event) => {
          event.stopPropagation();
          const showing = captionTrack.track.mode !== 'showing';
          captionTrack.track.mode = showing ? 'showing' : 'hidden';
          ccButton.setAttribute('aria-pressed', String(showing));
          ccButton.classList.toggle('is-on', showing);
        });
      } else if (ccButton) {
        ccButton.style.display = 'none';
      }

      let userStarted = false;

      const syncButton = () => {
        const engaged = userStarted && !video.paused;
        playIcon.style.display = engaged ? 'none' : '';
        pauseIcon.style.display = engaged ? '' : 'none';
        container?.classList.toggle('is-playing', engaged);
        playButton.setAttribute('aria-label', `${engaged ? 'Pause' : 'Play'} ${container.closest('.v20-ugc-card__media') ? 'customer' : 'expert'} video`);
      };


      playButton.addEventListener('click', (event) => {
        event.stopPropagation();
        loadUserVideo(video);
        if (video.paused || video.muted) {
          pauseOtherUserVideos(video);
          video.muted = false;
          video.currentTime = 0;
          video.play()?.catch?.(() => {});
          userStarted = true;
        } else {
          video.pause();
          video.muted = true;
          userStarted = false;
        }
        syncButton();
      });

      video.addEventListener('play', syncButton);
      video.addEventListener('pause', syncButton);
      video.addEventListener('ended', () => {
        userStarted = false;
        syncButton();
      });
      video.addEventListener('loadedmetadata', () => {
        time.textContent = `0:00 / ${formatTime(video.duration)}`;
      });
      video.addEventListener('timeupdate', () => {
        if (!userStarted) return;
        if (video.duration) seek.value = String((video.currentTime / video.duration) * 100);
        time.textContent = `${formatTime(video.currentTime)} / ${formatTime(video.duration)}`;
      });
      seek.addEventListener('input', (event) => {
        event.stopPropagation();
        userStarted = true;
        if (video.duration) video.currentTime = (Number(seek.value) / 100) * video.duration;
      });
      seek.addEventListener('click', (event) => event.stopPropagation());
      syncButton();
    });

    root.querySelectorAll?.('[data-v20-preview]').forEach((video) => {
      if (initialized.has(video) || !('IntersectionObserver' in window) || reduceMotion.matches) return;
      initialized.add(video);
      const loader = new IntersectionObserver(
        (entries, self) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            loadUserVideo(entry.target);
            self.disconnect();
          });
        },
        { rootMargin: '300px 0px' }
      );
      loader.observe(video);
      const previewer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const vid = entry.target;
            if (entry.isIntersecting) {
              if (vid.paused) vid.play()?.catch?.(() => {});
            } else if (!vid.paused && vid.muted) {
              vid.pause();
            }
          });
        },
        { threshold: 0.6 }
      );
      previewer.observe(video);
    });
  }

  function initializeBuySection(root) {
    root.querySelectorAll?.('.wt-featured-product .main-product__buy-buttons--container').forEach((el) => {
      el.setAttribute('data-buy-buttons-container', '');
      el.dataset.customerApproved = 'true';
    });
    root.querySelectorAll?.('[data-v20-onetime]').forEach((link) => {
      if (initialized.has(link)) return;
      initialized.add(link);
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const formId = link.dataset.v20Form;
        const form = document.getElementById(formId);
        if (!form) return;
        const radios = [...document.querySelectorAll(`input[name="selling_plan"][form="${formId}"]`)];
        const qty = document.querySelector('[data-v20-plan-qty]');
        const prevChecked = radios.map((r) => r.checked);
        const prevQty = qty ? qty.value : null;
        radios.forEach((r) => { r.checked = false; });
        if (qty) qty.value = '1';
        if (form.requestSubmit) form.requestSubmit(); else form.submit();
        setTimeout(() => {
          radios.forEach((r, i) => { r.checked = prevChecked[i]; });
          if (qty && prevQty !== null) qty.value = prevQty;
        }, 0);
      });
    });

    root.querySelectorAll?.('[data-v20-plans]').forEach((plans) => {
      if (initialized.has(plans)) return;
      initialized.add(plans);
      const qtyInput = plans.querySelector('[data-v20-plan-qty]');
      const popupStorageKey = 'gwp-home-monthly-popup-dismissed';
      let selectedMonth = Number(
        plans.querySelector('input[name="selling_plan"]:checked')?.dataset.v20Qty,
      );
      let panelCollapsed = false;

      const findPlanInput = (month) =>
        [...plans.querySelectorAll('input[name="selling_plan"]')].find(
          (input) => Number(input.dataset.v20Qty) === month,
        );

      const syncMonthlyPanel = () => {
        const existingPanel = plans.querySelector('xt-gwp-monthly-upsell');
        if (!window.themeSettings?.gwpOfferActive || !window.XtresseGwpMonthly) {
          existingPanel?.remove();
          return;
        }

        const checkedInput = plans.querySelector('input[name="selling_plan"]:checked');
        const checkedMonth = Number(checkedInput?.dataset.v20Qty);
        if ([3, 6].includes(checkedMonth)) panelCollapsed = false;

        const monthlyInput = findPlanInput(1);
        const monthlyOption = monthlyInput?.closest('.v20-buy__plan-wrap');
        if (!monthlyOption || checkedMonth !== 1 || panelCollapsed) {
          existingPanel?.remove();
          return;
        }

        if (existingPanel) {
          if (monthlyOption.nextElementSibling !== existingPanel) {
            monthlyOption.after(existingPanel);
          }
          return;
        }

        const panel = document.createElement('xt-gwp-monthly-upsell');
        panel.addEventListener('gwp:switch-to-three-months', () => {
          const threeMonthInput = findPlanInput(3);
          if (!threeMonthInput) return;
          threeMonthInput.focus();
          threeMonthInput.click();
        });
        panel.addEventListener('gwp:keep-monthly', () => {
          findPlanInput(1)?.focus();
          panelCollapsed = true;
          panel.remove();
        });
        monthlyOption.after(panel);
      };

      plans.addEventListener('change', (event) => {
        const radio = event.target.closest('input[name="selling_plan"]');
        if (!radio) return;

        const previousMonth = selectedMonth;
        selectedMonth = Number(radio.dataset.v20Qty);
        if (qtyInput) qtyInput.value = radio.dataset.v20Qty || '1';
        syncMonthlyPanel();

        if (selectedMonth === 1 && [3, 6].includes(previousMonth)) {
          window.XtresseGwpMonthly?.showPopup({
            storageKey: popupStorageKey,
            trigger: radio,
          });
        }
      });
      syncMonthlyPanel();
    });
  }

  function initializeHeroLoad(root) {
    root.querySelectorAll?.('.v20-hero [data-v20-load]').forEach((el) => {
      if (initialized.has(el)) return;
      initialized.add(el);
      const i = parseInt(el.getAttribute('data-v20-load'), 10) || 0;
      if (reduceMotion.matches) { el.classList.add('is-loaded'); return; }
      setTimeout(() => el.classList.add('is-loaded'), 140 + i * 110);
    });

    root.querySelectorAll?.('.v20-hero__video').forEach((media) => {
      if (media.dataset.v20ParallaxInit || reduceMotion.matches) return;
      media.dataset.v20ParallaxInit = '1';
      let queued = false;
      const update = () => {
        const y = window.scrollY;
        if (y < window.innerHeight * 1.3) {
          const s = 1.04 + Math.min(y / window.innerHeight, 1) * 0.06;
          media.style.transform = `translateY(${y * 0.18}px) scale(${s})`;
        }
        queued = false;
      };
      window.addEventListener('scroll', () => {
        if (!queued) { queued = true; requestAnimationFrame(update); }
      }, { passive: true });
    });
  }

  function initializeUgcScrollbar(root) {
    root.querySelectorAll?.('[data-v20-ugc-thumb]').forEach((thumb) => {
      if (initialized.has(thumb)) return;
      initialized.add(thumb);
      const gallery = thumb.closest('.v20-customer')?.querySelector('[data-v20-ugc-viewport]');
      if (!gallery) return;
      const sync = () => {
        const thumbPct = Math.min(100, (gallery.clientWidth / gallery.scrollWidth) * 100);
        const max = gallery.scrollWidth - gallery.clientWidth;
        const pct = max > 0 ? gallery.scrollLeft / max : 0;
        thumb.style.width = `${thumbPct}%`;
        thumb.style.left = `${pct * (100 - thumbPct)}%`;
      };
      gallery.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  function initializeCustomerCarousel(root) {
    root.querySelectorAll?.('[data-v20-section="social-proof"]').forEach((section) => {
      const viewport = section.querySelector('[data-v20-ugc-viewport]');
      const previous = section.querySelector('[data-v20-ugc-prev]');
      const next = section.querySelector('[data-v20-ugc-next]');
      if (!viewport || !previous || !next || initialized.has(viewport)) return;
      initialized.add(viewport);

      const step = () => {
        const card = viewport.querySelector('.v20-ugc-card');
        return card ? card.getBoundingClientRect().width + 22 : 300;
      };
      previous.addEventListener('click', () => viewport.scrollBy({ left: -step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      next.addEventListener('click', () => viewport.scrollBy({ left: step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
    });
  }

  function initializeExperts(root) {
    root.querySelectorAll?.('[data-v20-experts]').forEach((section) => {
      if (initialized.has(section)) return;
      initialized.add(section);

      const tabs = [...section.querySelectorAll('[data-v20-expert-tab]')];
      const panels = [...section.querySelectorAll('[data-v20-expert-panel]')];
      const previous = section.querySelector('[data-v20-expert-prev]');
      const next = section.querySelector('[data-v20-expert-next]');
      let activeIndex = 0;
      let animating = false;
      let started = false;

      const applyState = (focusTab) => {
        tabs.forEach((tab, tabIndex) => {
          const active = tabIndex === activeIndex;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          if (active && focusTab) tab.focus();
        });
        panels.forEach((panel, panelIndex) => {
          const active = panelIndex === activeIndex;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
          const video = panel.querySelector('video');
          if (!active) video?.pause();
          else if (video) loadUserVideo(video);
        });
      };

      const show = (index, focusTab = false, dir = null) => {
        const nextIndex = (index + tabs.length) % tabs.length;
        if (nextIndex === activeIndex && started) return;
        const direction = dir ?? (((nextIndex - activeIndex + tabs.length) % tabs.length) <= tabs.length / 2 ? 1 : -1);
        const outgoing = panels[activeIndex];
        activeIndex = nextIndex;

        if (!started || animating || reduceMotion.matches) {
          started = true;
          applyState(focusTab);
          return;
        }

        animating = true;
        outgoing.style.transition = 'transform 320ms cubic-bezier(.4,0,1,1), opacity 320ms ease';
        outgoing.style.transform = `translateX(${direction > 0 ? -32 : 32}px)`;
        outgoing.style.opacity = '0';
        setTimeout(() => {
          outgoing.style.transition = '';
          outgoing.style.transform = '';
          outgoing.style.opacity = '';
          applyState(focusTab);
          const incoming = panels[activeIndex];
          incoming.style.transition = 'none';
          incoming.style.transform = `translateX(${direction > 0 ? 32 : -32}px)`;
          incoming.style.opacity = '0';
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              incoming.style.transition = 'transform 380ms cubic-bezier(.22,1,.36,1), opacity 380ms ease';
              incoming.style.transform = 'translateX(0)';
              incoming.style.opacity = '1';
              setTimeout(() => {
                incoming.style.transition = '';
                incoming.style.transform = '';
                incoming.style.opacity = '';
                animating = false;
              }, 400);
            });
          });
        }, 300);
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => show(index, false, index > activeIndex ? 1 : -1));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'Home') show(0, true);
          else if (event.key === 'End') show(tabs.length - 1, true);
          else show(activeIndex + (event.key === 'ArrowRight' ? 1 : -1), true, event.key === 'ArrowRight' ? 1 : -1);
        });
      });
      previous?.addEventListener('click', () => show(activeIndex - 1, false, -1));
      next?.addEventListener('click', () => show(activeIndex + 1, false, 1));

      const THRESH = 50;
      let startX = 0;
      let startY = 0;
      let tracking = false;
      const dragStart = (x, y, media) => { startX = x; startY = y; tracking = true; media.classList.add('is-dragging'); };
      const dragEnd = (x, y, media) => {
        if (!tracking) return;
        tracking = false;
        media.classList.remove('is-dragging');
        const dx = x - startX;
        const dy = y - startY;
        if (Math.abs(dx) > THRESH && Math.abs(dx) > Math.abs(dy)) show(dx < 0 ? activeIndex + 1 : activeIndex - 1, false, dx < 0 ? 1 : -1);
      };
      panels.forEach((panel) => {
        const media = panel.querySelector('.v20-expert-panel__media');
        if (!media) return;
        media.addEventListener('touchstart', (e) => dragStart(e.touches[0].clientX, e.touches[0].clientY, media), { passive: true });
        media.addEventListener('touchend', (e) => dragEnd(e.changedTouches[0].clientX, e.changedTouches[0].clientY, media), { passive: true });
        media.addEventListener('mousedown', (e) => dragStart(e.clientX, e.clientY, media));
        media.addEventListener('mouseup', (e) => dragEnd(e.clientX, e.clientY, media));
        media.addEventListener('mouseleave', () => { tracking = false; media.classList.remove('is-dragging'); });
      });

      show(0);
    });
  }

  function initializeMarquees(root) {
    root.querySelectorAll?.('[data-v20-marquee-toggle]').forEach((button) => {
      if (initialized.has(button)) return;
      initialized.add(button);
      const track = button.closest('.v20-featured')?.querySelector('[data-v20-marquee-track]');
      if (!track) return;

      const playIcon = button.querySelector('[data-v20-play-icon]');
      const pauseIcon = button.querySelector('[data-v20-pause-icon]');
      const sync = (paused) => {
        if (playIcon) playIcon.style.display = paused ? '' : 'none';
        if (pauseIcon) pauseIcon.style.display = paused ? 'none' : '';
        button.setAttribute('aria-label', `${paused ? 'Play' : 'Pause'} featured publications`);
      };

      if (reduceMotion.matches) {
        track.classList.add('is-paused');
        sync(true);
      }

      button.addEventListener('click', () => {
        sync(track.classList.toggle('is-paused'));
      });
    });
  }

  function initialize(root = document) {
    if (!document.body?.classList.contains(PAGE_CLASS)) return;
    initializeCounters(root);
    initializeReveals(root);
    initializeVideos(root);
    initializeUserVideos(root);
    initializeUgcScrollbar(root);
    initializeHeroLoad(root);
    initializeBuySection(root);
    initializeCustomerCarousel(root);
    initializeExperts(root);
    initializeMarquees(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('video').forEach((video) => video.pause());
    if (counterObserver) {
      event.target.querySelectorAll?.('[data-v20-count]').forEach((counter) => counterObserver.unobserve(counter));
    }
    if (revealObserver) {
      event.target.querySelectorAll?.('[data-v20-reveal]').forEach((el) => revealObserver.unobserve(el));
    }
    if (sectionRevealObserver) {
      event.target.querySelectorAll?.('[data-v20-section-reveal]').forEach((el) => sectionRevealObserver.unobserve(el));
    }
  });

  reduceMotion.addEventListener?.('change', () => initialize());
})();
